import React, { useMemo } from 'react';

export default function PeriodoCalculoConsuModal({
  isOpen,
  onClose,
  onRecalcular,
  empleados = [],
  planillaNombre,
  fechaInicio,
  fechaFin,
  planillaCerrada = false,
  guardando = false,
  recalculando = false,
  fechaCalculoDesdeDe,
  fechaCalculoHastaDe,
  onCambiarFechas,
}) {
  const inicioPlanilla = String(fechaInicio || '').slice(0, 10);
  const finPlanilla = String(fechaFin || '').slice(0, 10);

  const empleadosOrdenados = useMemo(
    () =>
      [...empleados].sort((a, b) =>
        String(a.nombre_completo || a.nombre || '').localeCompare(
          String(b.nombre_completo || b.nombre || ''),
          'es'
        )
      ),
    [empleados]
  );

  const cantidadOverride = empleadosOrdenados.filter((emp) =>
    Boolean(fechaCalculoDesdeDe(emp.id) || fechaCalculoHastaDe(emp.id))
  ).length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
        <div className="bg-sky-600 text-white p-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">Periodo de cálculo</h2>
              <p className="text-sky-100 text-sm mt-1">
                Planilla: {planillaNombre || '—'} | {empleadosOrdenados.length} trabajador(es)
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-white hover:text-sky-100 transition-colors text-2xl leading-none"
              aria-label="Cerrar"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-8 overflow-y-auto max-h-[calc(90vh-200px)]">
          <div className="bg-sky-50 border border-sky-200 rounded-lg p-6 mb-6">
            <p className="text-sky-900 text-sm">
              Periodo general: {inicioPlanilla || '—'} a {finPlanilla || '—'}.
              Cambia por trabajador las fechas usadas para el cálculo, dentro de ese rango.
              Si dejas un campo vacío se usa la fecha de la planilla.
              Vuelve a generar la planilla después de cambiar las fechas.
            </p>
          </div>

          {cantidadOverride > 0 && (
            <div className="mb-4">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium bg-sky-100 text-sky-800 border border-sky-200">
                {cantidadOverride} trabajador(es) con fecha de cálculo distinta
              </span>
            </div>
          )}

          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Trabajador</th>
                  <th className="text-left px-4 py-3 font-medium">Desde</th>
                  <th className="text-left px-4 py-3 font-medium">Hasta</th>
                  <th className="text-left px-4 py-3 font-medium w-24"></th>
                </tr>
              </thead>
              <tbody>
                {empleadosOrdenados.map((emp) => {
                  const desde = fechaCalculoDesdeDe(emp.id);
                  const hasta = fechaCalculoHastaDe(emp.id);
                  const tieneOverride = Boolean(desde || hasta);
                  return (
                    <tr key={emp.id} className="border-t border-gray-100">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">
                          {emp.nombre_completo || emp.nombre || 'Empleado'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {emp.numero_documento || emp.documento || emp.dni || ''}
                          {tieneOverride ? ` · ${desde || inicioPlanilla} – ${hasta || finPlanilla}` : ''}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="date"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white"
                          min={inicioPlanilla || undefined}
                          max={finPlanilla || undefined}
                          value={desde}
                          disabled={guardando || planillaCerrada}
                          onChange={(e) => onCambiarFechas(emp.id, e.target.value, hasta)}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="date"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white"
                          min={inicioPlanilla || undefined}
                          max={finPlanilla || undefined}
                          value={hasta}
                          disabled={guardando || planillaCerrada}
                          onChange={(e) => onCambiarFechas(emp.id, desde, e.target.value)}
                        />
                      </td>
                      <td className="px-4 py-3">
                        {tieneOverride && (
                          <button
                            type="button"
                            className="text-xs text-sky-700 hover:text-sky-900 underline disabled:opacity-50"
                            disabled={guardando || planillaCerrada}
                            onClick={() => onCambiarFechas(emp.id, null, null)}
                          >
                            Quitar
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="px-8 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={guardando || recalculando}
            className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cerrar
          </button>
          <button
            type="button"
            disabled={guardando || recalculando || planillaCerrada}
            onClick={async () => {
              onClose();
              if (onRecalcular) await onRecalcular();
            }}
            className="px-5 py-2 text-sm font-medium text-white bg-sky-600 rounded-lg hover:bg-sky-700 disabled:opacity-50"
          >
            {recalculando ? 'Recalculando…' : 'Salir y recalcular'}
          </button>
        </div>
      </div>
    </div>
  );
}
